import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import { TrendingUp } from "lucide-react";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/primitives";
import { getFeelingArrows } from "@/utils/feeling-utils";
import { getFirstSentences } from "../utils";
import { TEXT_TRUNCATION } from "../constants";
import type { ChartData, ChartViewMode } from "../types";

interface MoodChartProps {
	data: ChartData[];
	viewMode: ChartViewMode;
	onViewModeChange: (mode: ChartViewMode) => void;
}

export function MoodChart({
	data,
	viewMode,
	onViewModeChange,
}: MoodChartProps) {
	return (
		<Card className="border-2 shadow-xl bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
							<TrendingUp className="h-5 w-5 text-white" />
						</div>
						<div>
							<CardTitle className="text-xl sm:text-2xl font-semibold">
								Overall mood trajectory
							</CardTitle>
							<CardDescription>
								Track your emotional journey over time
							</CardDescription>
						</div>
					</div>
					<div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
						<Button
							variant={viewMode === "days" ? "secondary" : "outline"}
							size="sm"
							onClick={() => onViewModeChange("days")}
							className="w-full sm:w-auto"
						>
							28 Days
						</Button>
						<Button
							variant={viewMode === "entries" ? "secondary" : "outline"}
							size="sm"
							onClick={() => onViewModeChange("entries")}
							className="w-full sm:w-auto"
						>
							30 Entries
						</Button>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				{data.length === 0 ? (
					<div className="h-32 sm:h-[171px] flex items-center justify-center text-center">
						<div className="text-muted-foreground">
							<p className="text-sm font-medium mb-1">No check-in data yet</p>
							<p className="text-xs">
								Start tracking your mood by adding your first check-in
							</p>
						</div>
					</div>
				) : (
					<div className="h-32 sm:h-[171px]">
						<ResponsiveContainer width="100%" height="100%">
							<LineChart
								data={data}
								margin={{ left: 0, right: 5, top: 5, bottom: 5 }}
							>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="date" />
								<YAxis tick={false} width={0} axisLine={false} />
								<Tooltip
									content={({ active, payload, label }) => {
										if (!active || !payload || payload.length === 0)
											return null;
										const chartData = payload[0].payload as ChartData;
										const arrows = getFeelingArrows(chartData.generalFeeling);
										const description =
											chartData.description &&
											chartData.description.trim().length > 0
												? getFirstSentences(
														chartData.description,
														TEXT_TRUNCATION.SENTENCES_THREE,
													)
												: "No description available";

										return (
											<div className="rounded-md border bg-popover px-3 py-2 text-xs sm:text-sm text-popover-foreground shadow-md max-w-xs sm:max-w-sm">
												<div className="flex items-center justify-between mb-1">
													<span className="font-medium">{label}</span>
													<span className="flex items-center">{arrows}</span>
												</div>
												<p className="text-muted-foreground leading-snug">
													{description}
												</p>
												{chartData.reflectionId && (
													<div className="mt-2 text-[11px] sm:text-xs text-foreground">
														Reflection added
													</div>
												)}
											</div>
										);
									}}
								/>
								<Line
									type="monotone"
									dataKey="feeling"
									stroke="#3b82f6"
									strokeWidth={2}
									dot={{ fill: "#3b82f6", strokeWidth: 2, r: 2 }}
								/>
								<Line
									type="monotone"
									dataKey="rollingAverage"
									stroke="#10b981"
									strokeWidth={2}
									strokeDasharray="5 5"
									dot={false}
								/>
							</LineChart>
						</ResponsiveContainer>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
